// src/pages/LandingPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
  animate,
  AnimatePresence,
} from 'framer-motion';
import {
  ShieldCheck,
  MailWarning,
  BarChart2,
  Trophy,
  Users,
  GaugeCircle,
  KeyRound,
  Gift,
  Phone,
  Mail,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SwipeCard from '../components/SwipeCard';
import FeedBackCard from '../components/FeedbackCard';
import { getLeadEndpoint } from '../utils/leadsApi';

function LanguageToggle() {
  const { i18n } = useTranslation();
  const lng = (i18n.language || 'en').toLowerCase();
  const current = lng.startsWith('nl') ? 'nl' : lng.startsWith('fr') ? 'fr' : 'en';

  const switchTo = (lang) => {
    i18n.changeLanguage(lang);
    try { localStorage.setItem('lang', lang); } catch {}
    document.documentElement.setAttribute('lang', lang);
  };

  return (
    <div className="inline-flex items-center gap-2 rounded-md bg-white/5 px-2 py-1">
      <button
        className={`text-xs px-2 py-1 rounded ${current === 'nl' ? 'bg-white/20' : 'hover:bg-white/10'}`}
        onClick={() => switchTo('nl')}
        aria-pressed={current === 'nl'}
      >
        NL
      </button>
      <button
        className={`text-xs px-2 py-1 rounded ${current === 'en' ? 'bg-white/20' : 'hover:bg-white/10'}`}
        onClick={() => switchTo('en')}
        aria-pressed={current === 'en'}
      >
        EN
      </button>
      <button
        className={`text-xs px-2 py-1 rounded ${current === 'fr' ? 'bg-white/20' : 'hover:bg-white/10'}`}
        onClick={() => switchTo('fr')}
        aria-pressed={current === 'fr'}
      >
        FR
      </button>
    </div>
  );
}

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const cardControls = useAnimation();
  const heroControls = useAnimation();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-20, 20]);
  const leadEndpoint = useMemo(() => getLeadEndpoint(), []);

  // Demo emails (NL, EN, FR)
  const demoEmails = useMemo(() => {
    const lng = (i18n.language || 'en').toLowerCase();
    const isNL = lng.startsWith('nl');
    const isFR = lng.startsWith('fr');

    if (isNL) {
      return [
        {
          id: 101,
          subject: 'Ongebruikelijke aanmeldingsactiviteit gedetecteerd',
          sender: 'Microsoft Beveiliging <security@micr0s0ft.com>',
          department: 'Microsoft Beveiliging',
          phishing: true,
          content: `
            <div style="text-align:center">
              <img src="/images/microsoft.svg" alt="Microsoft" style="display:block;margin:0 auto;height:90px;margin-bottom:8px" />
              <p style="font-size:15px;line-height:1.6;color:#333">We merkten een aanmeldingspoging op vanaf een nieuw apparaat of locatie.</p>
              <p style="font-size:15px;line-height:1.6;color:#333">Controleer je accountinstellingen meteen.</p>
              <div style="margin:20px 0">
                <a href="https://micr0s0ft.com/login" style="background:#007bff;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Activiteit controleren</a>
              </div>
              <p style="font-size:12px;color:#666">Geconstateerd: 05/06/2025 10:42 (CET)</p>
            </div>
          `
        },
        {
          id: 104,
          subject: 'Belangrijke beveiligingsupdate vereist, vandaag uitvoeren',
          sender: 'IT Beheer <it@contoso-security-updates.com>',
          department: 'IT Beheer',
          phishing: true,
          content: `
            <div style="text-align:center">
              <p style="font-size:15px;line-height:1.6;color:#333">Goedemorgen, er is een kritieke update beschikbaar voor jouw toestel.</p>
              <p style="font-size:15px;line-height:1.6;color:#333">Klik hieronder om de update direct te starten.</p>
              <div style="margin:20px 0">
                <a href="https://contoso-security-updates.com/update" style="background:#ffc107;color:#000;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Start update</a>
              </div>
              <p style="font-size:12px;color:#666">Update-ID: KB509821</p>
            </div>
          `
        },
        {
          id: 108,
          subject: 'Je Bpost-levering is gepland voor morgen',
          sender: 'Bpost Klantenservice <info@bpost.be>',
          department: 'Bpost',
          phishing: false,
          content: `
            <div style="text-align:center">
              <img src="/images/bpost.svg" alt="Bpost" style="display:block;margin:0 auto 12px;height:70px" />
              <p style="font-size:15px;line-height:1.6;color:#333">Je pakket wordt morgen geleverd tussen 09:00 en 13:00.</p>
              <p style="font-size:15px;line-height:1.6;color:#333">Volg je zending via de knop hieronder.</p>
              <div style="margin:20px 0">
               <a href="https://track.bpost.be/pakket" style="background:#dc3545;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Volg mijn pakket</a>
              </div>
              <p style="font-size:12px;color:#666">Trackingsnummer: 1ZA84XF993712736*</p>
            </div>
          `
        }
      ];
    }

    if (isFR) {
      return [
        {
          id: 301,
          subject: 'Activité de connexion inhabituelle détectée',
          sender: 'Sécurité Microsoft <security@micr0s0ft.com>',
          department: 'Sécurité Microsoft',
          phishing: true,
          content: `
            <div style="text-align:center">
              <img src="/images/microsoft.svg" alt="Microsoft" style="display:block;margin:0 auto;height:90px;margin-bottom:8px" />
              <p style="font-size:15px;line-height:1.6;color:#333">Nous avons détecté une tentative de connexion depuis un nouvel appareil ou une nouvelle localisation.</p>
              <p style="font-size:15px;line-height:1.6;color:#333">Veuillez vérifier vos paramètres de sécurité maintenant.</p>
              <div style="margin:20px 0">
                <a href="https://micr0s0ft.com/login" style="background:#007bff;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Vérifier l’activité</a>
              </div>
              <p style="font-size:12px;color:#666">Détecté : 05/06/2025 10:42 (CET)</p>
            </div>
          `
        },
        {
          id: 304,
          subject: 'Mise à jour de sécurité importante requise, à effectuer aujourd’hui',
          sender: 'Support IT <it@contoso-security-updates.com>',
          department: 'Support IT',
          phishing: true,
          content: `
            <div style="text-align:center">
              <p style="font-size:15px;line-height:1.6;color:#333">Une mise à jour critique est disponible pour votre poste de travail.</p>
              <p style="font-size:15px;line-height:1.6;color:#333">Cliquez ci-dessous pour démarrer la mise à jour immédiatement.</p>
              <div style="margin:20px 0">
                <a href="https://contoso-security-updates.com/update" style="background:#ffc107;color:#000;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Démarrer la mise à jour</a>
              </div>
              <p style="font-size:12px;color:#666">ID de mise à jour : KB509821</p>
            </div>
          `
        },
        {
          id: 308,
          subject: 'Votre livraison Bpost est prévue pour demain',
          sender: 'Service client Bpost <info@bpost.be>',
          department: 'Bpost',
          phishing: false,
          content: `
            <div style="text-align:center">
              <img src="/images/bpost.svg" alt="Bpost" style="display:block;margin:0 auto 12px;height:70px" />
              <p style="font-size:15px;line-height:1.6;color:#333">Votre colis sera livré demain entre 09:00 et 13:00.</p>
              <p style="font-size:15px;line-height:1.6;color:#333">Suivez votre envoi via le bouton ci-dessous.</p>
              <div style="margin:20px 0">
                <a href="https://track.bpost.be/pakket" style="background:#dc3545;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Suivre mon colis</a>
              </div>
              <p style="font-size:12px;color:#666">Numéro de suivi : 1ZA84XF993712736*</p>
            </div>
          `
        }
      ];
    }

    // EN
    return [
      {
        id: 201,
        subject: 'Unusual sign-in activity detected',
        sender: 'Microsoft Security <security@micr0s0ft.com>',
        department: 'Microsoft Security',
        phishing: true,
        content: `
          <div style="text-align:center">
            <img src="/images/microsoft.svg" alt="Microsoft" style="display:block;margin:0 auto;height:90px;margin-bottom:8px" />
            <p style="font-size:15px;line-height:1.6;color:#333">We noticed a sign-in attempt from a new device or location.</p>
            <p style="font-size:15px;line-height:1.6;color:#333">Please review your security settings now.</p>
            <div style="margin:20px 0">
              <a href="https://micr0s0ft.com/login" style="background:#007bff;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Review activity</a>
           </div>
            <p style="font-size:12px;color:#666">Detected: Jun 5, 2025 10:42 AM (CET)</p>
         </div>
        `
      },
      {
        id: 204,
        subject: 'Important security update required, complete today',
        sender: 'IT Support <it@contoso-security-updates.com>',
        department: 'IT Support',
        phishing: true,
        content: `
          <div style="text-align:center">
            <p style="font-size:15px;line-height:1.6;color:#333">A critical workstation update is available.</p>
            <p style="font-size:15px;line-height:1.6;color:#333">Click below to start the update immediately.</p>
            <div style="margin:20px 0">
              <a href="https://contoso-security-updates.com/update" style="background:#ffc107;color:#000;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Start update</a>
            </div>
           <p style="font-size:12px;color:#666">Update ID: KB509821</p>
          </div>
        `
      },
      {
        id: 208,
        subject: 'Your Bpost delivery is scheduled for tomorrow',
        sender: 'Bpost Customer Service <info@bpost.be>',
        department: 'Bpost',
        phishing: false,
        content: `
          <div style="text-align:center">
            <img src="/images/bpost.svg" alt="Bpost" style="display:block;margin:0 auto 12px;height:70px" />
            <p style="font-size:15px;line-height:1.6;color:#333">Your package will be delivered tomorrow between 09:00 and 13:00.</p>
            <p style="font-size:15px;line-height:1.6;color:#333">Track your shipment below.</p>
            <div style="margin:20px 0">
              <a href="https://track.bpost.be/pakket" style="background:#dc3545;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600">Track my package</a>
            </div>
            <p style="font-size:12px;color:#666">Tracking number: 1ZA84XF993712736*</p>
          </div>
        `
      }
    ];
  }, [i18n.language]);

  // Feedback content for FeedBackCard (EN, NL, FR)
  const feedbackByLanguage = useMemo(() => {
    const todayEN = new Date().toLocaleDateString('en-GB');
    const todayNL = new Date().toLocaleDateString('nl-BE');
    const todayFR = new Date().toLocaleDateString('fr-FR');
    return {
      en: {
        microsoft: {
          subject: 'Unusual sign-in activity detected',
          sender: `<p class="malicious" title="Suspicious sender with lookalike domain">Microsoft Security &lt;security@micr0s0ft.com&gt;</p>`,
          content: `
  <div style="text-align:center;">
    <img src="/images/microsoft.svg" alt="Microsoft logo" style="display:block; margin: 0 auto; height: 150px;" />
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">
      We detected a sign-in attempt on your Microsoft account from a new device or location.
    </p>
    <p style="font-size:15px; line-height:1.6; color:#333;">
      We recommend you check your account settings immediately.
    </p>
    <div style="margin: 24px 0;">
      <a
        href="https://micr0s0ft.com/login"
        class="malicious"
        title="Caution: suspicious domain"
        style="background:#007bff;color:white;padding:12px 20px;border-radius:4px;font-weight:bold;"
      >
        Check activity
      </a>
    </div>
    <p style="font-size:13px; color:#666;">Activity detected: 05 June 2025 at 10:42 (CET)</p>
  </div>`,
          footer: `
  <div style="font-size:12px; color:#888; padding-top:10px; text-align:center;">
    This email was generated automatically. Do not reply directly.<br />
    Visit your <a href="https://account.microsoft.com/security" style="color:#dc3545; text-decoration:underline;">security settings</a> for more info.
  </div>`
        },
        it: {
          subject: 'Important security update required, complete today',
          sender: `<p class="malicious" title="Sender domain differs from internal IT management">IT Admin &lt;it@contoso-security-updates.com&gt;`,
          content: `
  <div style="text-align:center;">
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">Good morning,</p>
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">
      An important security update is available for your workstation. To minimize risk, we ask you to install it today.
    </p>
    <p style="font-size:15px; line-height:1.6; color:#333;">
      Click the button below to start the update immediately.
    </p>
    <div style="margin: 24px 0;">
      <a
        href="https://contoso-security-updates.com/update"
        class="malicious"
        title="Domain does not match your organization"
        style="background:#ffc107; color:black; padding:12px 20px; border-radius:4px; font-weight:bold;"
      >
        Start update
      </a>
    </div>
    <p style="font-size:13px; color:#666;">
      Update ID: KB509821, available since ${todayEN}
    </p>
  </div>`,
          footer: `
  <div style="font-size:12px; color:#888; padding-top:10px; text-align:center;">
    Questions? Contact <a href="mailto:it@contoso-security-updates.com" style="text-decoration:underline;color:#ffc107;">it@contoso-security-updates.com</a><br />
    This message was automatically sent from the IT management system.
  </div>`
        }
      },
      nl: {
        microsoft: {
          subject: 'Ongebruikelijke aanmeldingsactiviteit gedetecteerd',
          sender: `<p class="malicious" title="Verdachte afzender met lookalike domein">Microsoft Beveiliging &lt;security@micr0s0ft.com&gt;</p>`,
          content: `
   <div style="text-align:center;">
      <img src="/images/microsoft.svg" alt="Microsoft logo" style="display:block; margin: 0 auto; height: 150px;" />
      <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">
        We hebben een poging tot aanmelden op je Microsoft-account opgemerkt vanaf een nieuw apparaat of locatie.
      </p>
      <p style="font-size:15px; line-height:1.6; color:#333;">
        We raden je aan je accountinstellingen onmiddellijk te controleren.
      </p>
      <div style="margin: 24px 0;">
        <a
          href="https://micr0s0ft.com/login"
          class="malicious"
          title="Opgelet: verdacht domein"
          style="background:#007bff;color:white;padding:12px 20px;border-radius:4px;font-weight:bold;"
        >
          Controleer activiteit
        </a>
      </div>
      <p style="font-size:13px; color:#666;">Activiteit gedetecteerd: 05 juni 2025 om 10:42 (CET)</p>
    </div>`,
          footer: `
  <div style="font-size:12px; color:#888; padding-top:10px; text-align:center;">
    Deze e-mail is automatisch gegenereerd. Reageer niet rechtstreeks op dit bericht.<br />
    Bezoek je <a href="https://account.microsoft.com/security" style="color:#dc3545; text-decoration:underline;">beveiligingsinstellingen</a> voor meer informatie.
  </div>`
        },
        it: {
          subject: 'Belangrijke beveiligingsupdate vereist, vandaag uitvoeren',
          sender: `<p class="malicious" title="Afzenderdomein wijkt af van intern IT-beheer">IT Beheer &lt;it@contoso-security-updates.com&gt;`,
          content: `
  <div style="text-align:center;">
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">Goedemorgen,</p>
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">
      Er is een belangrijke beveiligingsupdate beschikbaar voor jouw werkstation. Om risico’s te beperken, vragen we om deze vandaag nog uit te voeren.
    </p>
    <p style="font-size:15px; line-height:1.6; color:#333;">
      Klik op onderstaande knop om de update direct te starten.
    </p>
    <div style="margin: 24px 0;">
      <a
        href="https://contoso-security-updates.com/update"
        class="malicious"
        title="Domein komt niet overeen met je organisatie"
        style="background:#ffc107;color:black;padding:12px 20px;border-radius:4px;font-weight:bold;"
      >
        Start update
      </a>
    </div>
    <p style="font-size:13px; color:#666;">
      Update-ID: KB509821, beschikbaar sinds ${todayNL}
    </p>
  </div>`,
          footer: `
  <div style="font-size:12px; color:#888; padding-top:10px; text-align:center;">
    Heb je vragen? Contacteer <a href="mailto:it@contoso-security-updates.com" style="text-decoration:underline;color:#ffc107;">it@contoso-security-updates.com</a><br />
    Deze melding werd automatisch verstuurd vanuit het IT-beheersysteem.
  </div>`
        }
      },
      fr: {
        microsoft: {
          subject: 'Activité de connexion inhabituelle détectée',
          sender: `<p class="malicious" title="Expéditeur suspect avec domaine ressemblant">Sécurité Microsoft &lt;security@micr0s0ft.com&gt;</p>`,
          content: `
  <div style="text-align:center;">
    <img src="/images/microsoft.svg" alt="Logo Microsoft" style="display:block; margin: 0 auto; height: 150px;" />
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">
      Nous avons détecté une tentative de connexion à votre compte Microsoft depuis un nouvel appareil ou une nouvelle localisation.
    </p>
    <p style="font-size:15px; line-height:1.6; color:#333;">
      Nous vous recommandons de vérifier vos paramètres de sécurité immédiatement.
    </p>
    <div style="margin: 24px 0;">
      <a
        href="https://micr0s0ft.com/login"
        class="malicious"
        title="Attention, domaine suspect"
        style="background:#007bff;color:white;padding:12px 20px;border-radius:4px;font-weight:bold;"
      >
        Vérifier l’activité
      </a>
    </div>
    <p style="font-size:13px; color:#666;">Activité détectée : 05 juin 2025 à 10:42 (CET)</p>
  </div>`,
          footer: `
  <div style="font-size:12px; color:#888; padding-top:10px; text-align:center;">
    Cet e-mail a été généré automatiquement. Ne répondez pas directement.<br />
    Consultez vos <a href="https://account.microsoft.com/security" style="color:#dc3545; text-decoration:underline;">paramètres de sécurité</a> pour en savoir plus.
  </div>`
        },
        it: {
          subject: 'Mise à jour de sécurité importante requise, à effectuer aujourd’hui',
          sender: `<p class="malicious" title="Le domaine de l’expéditeur diffère de l’IT interne">Admin IT &lt;it@contoso-security-updates.com&gt;`,
          content: `
  <div style="text-align:center;">
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">Bonjour,</p>
    <p class="pb-4" style="font-size:15px; line-height:1.6; color:#333;">
      Une mise à jour de sécurité importante est disponible pour votre poste de travail. Pour réduire les risques, merci de l’installer aujourd’hui.
    </p>
    <p style="font-size:15px; line-height:1.6; color:#333;">
      Cliquez sur le bouton ci-dessous pour démarrer la mise à jour immédiatement.
    </p>
    <div style="margin: 24px 0;">
      <a
        href="https://contoso-security-updates.com/update"
        class="malicious"
        title="Le domaine ne correspond pas à votre organisation"
        style="background:#ffc107; color:black; padding:12px 20px; border-radius:4px; font-weight:bold;"
      >
        Démarrer la mise à jour
      </a>
    </div>
    <p style="font-size:13px; color:#666;">
      ID de mise à jour : KB509821, disponible depuis le ${todayFR}
    </p>
  </div>`,
          footer: `
  <div style="font-size:12px; color:#888; padding-top:10px; text-align:center;">
    Des questions ? Contactez <a href="mailto:it@contoso-security-updates.com" style="text-decoration:underline;color:#ffc107;">it@contoso-security-updates.com</a><br />
    Ce message a été envoyé automatiquement par le système de gestion IT.
  </div>`
        }
      }
    };
  }, []);

  // Map demo email -> feedback block
  const getFeedbackForEmail = (email) => {
    const lng = (i18n.language || 'en').toLowerCase();
    const lang = lng.startsWith('nl') ? 'nl' : lng.startsWith('fr') ? 'fr' : 'en';
    if (!email?.phishing) return null;

    const subj = (email.subject || '').toLowerCase();
    // Match sign-in keywords in EN/NL/FR
    if (
      subj.includes('sign-in') ||
      subj.includes('aanmeld') ||
      subj.includes('ongebruikelijke') ||
      subj.includes('connexion') ||
      subj.includes('inhabituelle')
    ) {
      const b = feedbackByLanguage[lang].microsoft;
      return { subject: b.subject, sender: b.sender, content: b.content, footer: b.footer, department: email.department || '' };
    }
    if (subj.includes('update') || subj.includes('mise à jour') || subj.includes('beveiligingsupdate')) {
      const b = feedbackByLanguage[lang].it;
      return { subject: b.subject, sender: b.sender, content: b.content, footer: b.footer, department: email.department || '' };
    }
    return null;
  };

  // Labels passed to FeedBackCard
  const feedbackLabels = useMemo(() => {
    const lng = (i18n.language || 'en').toLowerCase();
    const isNL = lng.startsWith('nl');
    const isFR = lng.startsWith('fr');
    if (isNL) {
      return {
        phishingReasonTitle: 'Dit bericht is onveilig',
        phishingReasonDesc: 'Let op de rode markeringen hieronder. Ze tonen waarom dit bericht verdacht is.',
        continueButton: 'Doorgaan',
      };
    }
    if (isFR) {
      return {
        phishingReasonTitle: 'Ce message est dangereux',
        phishingReasonDesc: 'Regardez les surlignages rouges ci-dessous. Ils montrent pourquoi ce message est suspect.',
        continueButton: 'Continuer',
      };
    }
    return {
      phishingReasonTitle: 'This message is unsafe',
      phishingReasonDesc: 'Watch for the red highlights below. They show why this message is suspicious.',
      continueButton: 'Continue',
    };
  }, [i18n.language]);

  // Toast (missed legitimate swipe)
  const [missedLegitMsg, setMissedLegitMsg] = useState(null);
  const missedLegitMessage = useMemo(() => {
    const lng = (i18n.language || 'en').toLowerCase();
    if (lng.startsWith('nl')) return '❌ Oeps. Dit was een legitieme e-mail';
    if (lng.startsWith('fr')) return '❌ Oups. Cet e-mail était légitime';
    return '❌ Oops. This was a legitimate email';
  }, [i18n.language]);

  const showMissedLegitToast = () => {
    setMissedLegitMsg(missedLegitMessage);
    setTimeout(() => setMissedLegitMsg(null), 2200);
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const email = demoEmails[currentIndex];
  const [forcedDirection, setForcedDirection] = useState(null);

  // Feedback modal state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState(null);

  useEffect(() => {
    cardControls.start({ opacity: 1, y: 0 });
    heroControls.start({ opacity: 1, y: 0 });
  }, [cardControls, heroControls]);

  // Lock body scroll when modal open (mobile polish)
  useEffect(() => {
    if (showFeedback) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [showFeedback]);

  useEffect(() => {
    if (!forcedDirection) return;
    const dir = forcedDirection === 'right' ? 300 : -300;
    animate(x, dir, {
      type: 'spring',
      stiffness: 180,
      damping: 22,
      mass: 0.6,
      onComplete: () => handleSwipeComplete(forcedDirection),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedDirection]);

  const handleSwipeComplete = (dir) => {
    setForcedDirection(null);
    const userSaysPhishing = dir === 'left';
    const isPhishing = !!email?.phishing;
    const isCorrect = (userSaysPhishing && isPhishing) || (!userSaysPhishing && !isPhishing);

    if (isCorrect) {
      setScore((s) => s + 1);
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        x.set(0);
      }, 120);
      return;
    }

    // Wrong and actually unsafe → show feedback card
    if (!userSaysPhishing && isPhishing) {
      const fb = getFeedbackForEmail(email);
      if (fb) {
        setFeedbackEmail(fb);
        setShowFeedback(true);
        return;
      }
    }

    // Wrong and actually legit → toast
    if (userSaysPhishing && !isPhishing) {
      showMissedLegitToast();
    }

    // Advance as usual
    setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
      x.set(0);
    }, 120);
  };

  const handleDragEnd = (_e, info) => {
    if (info.offset.x > 150) {
      animate(x, 300, { type: 'spring', stiffness: 180, damping: 22, mass: 0.6, onComplete: () => handleSwipeComplete('right') });
    } else if (info.offset.x < -150) {
      animate(x, -300, { type: 'spring', stiffness: 180, damping: 22, mass: 0.6, onComplete: () => handleSwipeComplete('left') });
    } else {
      x.set(0);
    }
  };

  const MaliciousStyle = () => (
    <style>{`
      .malicious {
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-decoration-color: #dc3545;
        cursor: help;
      }
      .size-4 { width: 1rem; height: 1rem; }
    `}</style>
  );

  return (
    <div className="min-h-screen bg-transparent text-white font-sans flex flex-col relative overflow-hidden">
      <MaliciousStyle />

      {/* Background blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div
          className="absolute w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl top-6 left-[-120px]"
          animate={{ y: [0, 30, 0], x: [0, 30, 0] }}
          transition={{ repeat: Infinity, duration: 10, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-96 h-96 bg-rose-400/20 rounded-full blur-3xl bottom-8 right-[-120px]"
          animate={{ y: [0, -30, 0], x: [0, -30, 0] }}
          transition={{ repeat: Infinity, duration: 12, ease: 'easeInOut' }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-20 w-full ">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-14 max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Swishing Logo" className="h-8 w-auto" />
            <span className="sr-only">Swishing</span>
          </Link>
          <nav className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-6 text-sm text-white/70">
              <a href="#features" className="hover:text-white">{t('nav.features')}</a>
              <a href="#contact" className="hover:text-white">{t('nav.contact')}</a>
            </div>
            <LanguageToggle />
            <a
              href="https://auth.swishing.cards/login"
              className="rounded-md bg-[#00aca9] hover:bg-[#00908f] text-white font-medium px-3.5 py-2 transition"
            >
              {t('cta.login')}
            </a>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="relative flex-1 w-full px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto flex flex-col items-center justify-center py-14 z-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={heroControls}
          transition={{ duration: 0.7 }}
          className="text-center mb-10 max-w-4xl mx-auto"
        >
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-8">
            {t('hero.title1')}
          </h1>
          <p className="text-2xl sm:text-3xl font-semibold text-white/90 mb-4">
            {t('hero.title2')}
          </p>
          <p className="text-white/70 mb-6 text-base sm:text-lg">
            {t('hero.lead')}
          </p>
          <div className="flex items-center justify-center gap-3">
            <a
              href="https://app.swishing.cards"
              className="bg-[#00aca9] hover:bg-[#00908f] text-white px-6 py-3 rounded-xl font-semibold shadow transition"
            >
              {t('cta.start')}
            </a>
          </div>
        </motion.div>

        {/* Swipe demo */}
        <section
          id="demo"
          aria-label={t('demo.aria', { defaultValue: t('demo.tryTitle') })}
          className="w-full max-w-3xl mx-auto mt-2 mb-12"
        >
          <header className="mb-5 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">{t('demo.tryTitle')}</h2>
            <p className="text-white/70 text-sm sm:text-base mt-1">{t('demo.trySubtitle')}</p>
            <p className="mt-2 text-white/80 text-sm">{t('demo.score', { score, total: demoEmails.length })}</p>
          </header>

          {currentIndex < demoEmails.length ? (
            <div className="flex flex-col items-center gap-3">
              <SwipeCard
                key={email?.id ?? currentIndex}
                email={{
                  id: email.id,
                  subject: email.subject,
                  department: email.department,
                  sender: email.sender,
                  content: email.content,
                  footer: email.footer || '',
                  phishing: !!email.phishing,
                }}
                onSwipe={(dir) => handleSwipeComplete(dir)}
                forcedDirection={forcedDirection}
                onDragEnd={handleDragEnd}
                receiverEmail="you@sectricity.com"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => setForcedDirection('left')}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm px-3 py-1.5 rounded"
                >
                  {t('demo.btnPhishing')}
                </button>
                <button
                  onClick={() => setForcedDirection('right')}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm px-3 py-1.5 rounded"
                >
                  {t('demo.btnLegit')}
                </button>
              </div>

              <p className="text-xs text-white/60">
                {t('demo.remaining', { count: demoEmails.length - currentIndex })}
              </p>
            </div>
          ) : (
            <div className="text-center text-white/80 text-sm">
              {t('demo.done')}
            </div>
          )}
        </section>

        {/* Features */}
        <section id="features" className="w-full">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ staggerChildren: 0.12 }}
            className="grid gap-6 md:grid-cols-2 xl:grid-cols-4"
          >
            <Feature Icon={MailWarning} title={t('features.1.title')} description={t('features.1.desc')} />
            <Feature Icon={ShieldCheck} title={t('features.2.title')} description={t('features.2.desc')} />
            <Feature Icon={GaugeCircle} title={t('features.3.title')} description={t('features.3.desc')} />
            <Feature Icon={Trophy} title={t('features.5.title')} description={t('features.5.desc')} />
            <Feature Icon={Users} title={t('features.4.title')} description={t('features.4.desc')} />
            <Feature Icon={BarChart2} title={t('features.6.title')} description={t('features.6.desc')} />
            <Feature Icon={KeyRound} title={t('features.7.title')} description={t('features.7.desc')} />
            <Feature Icon={Gift} title={t('features.8.title')} description={t('features.8.desc')} featured />
          </motion.div>
        </section>
      </main>

      {/* Lead capture */}
      <section id="contact" className="relative z-10 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1.05fr,0.95fr] items-stretch">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-8 md:p-10 shadow-[0_30px_80px_rgba(15,23,42,0.45)]">
              <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                Swishing Cards
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-white">
                {t('leadForm.title')}
              </h2>
              <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed">
                {t('leadForm.subtitle')}
              </p>
              <div className="mt-6 grid gap-4 text-sm text-white/70">
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                  <span>{t('leadForm.items.1')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                  <span>{t('leadForm.items.2')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                  <span>{t('leadForm.items.3')}</span>
                </div>
              </div>
            </div>

            <LeadCapture t={t} leadEndpoint={leadEndpoint} />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-white/[.04] text-center">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid gap-10 md:grid-cols-2 text-white/80 place-items-center text-center">
            <div className="max-w-md mx-auto">
              <h3 className="text-2xl font-extrabold text-white mb-3">
                {t('footer.be_nl')}
              </h3>
              <p className="font-semibold text-white mb-1">{t('footer.company')}</p>
              <p className="leading-relaxed">
                Gaversesteenweg 94<br />
                9820 Merelbeke-Melle
              </p>
              <div className="mt-3 space-y-1">
                <p className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="font-semibold">BE:</span>
                  <a href="tel:+3292980585" className="hover:text-white">+32 9 298 05 85</a>
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="font-semibold">NL:</span>
                  <a href="tel:+31852087871" className="hover:text-white">+31 85 208 7871</a>
                </p>
              </div>
              <p className="mt-3 flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                <a href="mailto:info@sectricity.com" className="hover:text-white">info@sectricity.com</a>
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <h3 className="text-2xl font-extrabold text-white mb-3">
                {t('footer.uk_us')}
              </h3>
              <p className="font-semibold text-white mb-1">{t('footer.company')}</p>
              <p className="leading-relaxed">
                1 Coldbath Square, Farringdon<br />
                London EC1R 5HL
              </p>
              <div className="mt-3 space-y-1">
                <p className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="font-semibold">UK:</span>
                  <a href="tel:+447383833041" className="hover:text-white">+44 7383 833041</a>
                </p>
                <p className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
                  <span className="font-semibold">US:</span>
                  <a href="tel:+16466872163" className="hover:text-white">+1 (646) 687 2163</a>
                </p>
              </div>
              <p className="mt-3 flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                <a href="mailto:info@sectricity.com" className="hover:text-white">info@sectricity.com</a>
              </p>
            </div>
          </div>

          <div className="mt-10 text-center text-white/40 text-xs">
            © {new Date().getFullYear()} Swishing Cards - Powered by <a href='https://sectricity.com/en/sectricity-game-phishing/'>Sectricity</a>
          </div>
        </div>
      </footer>

      {/* FEEDBACK MODAL (mobile-friendly) */}
      {showFeedback && feedbackEmail && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="fixed inset-0 bg-black/60" />
          <div className="relative z-10 min-h-[100dvh] flex items-start sm:items-center justify-center">
            <div className="w-full flex justify-center py-4 sm:py-8">
              <FeedBackCard
                email={feedbackEmail}
                receiverEmail="you@sectricity.com"
                t={feedbackLabels}
                onClose={() => {
                  setShowFeedback(false);
                  setFeedbackEmail(null);
                  setCurrentIndex((prev) => prev + 1);
                  x.set(0);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Missed-legit toast (center top) */}
      <AnimatePresence>
        {missedLegitMsg && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="
        fixed inset-0 z-[60]
        flex items-center justify-center
        px-4 pointer-events-none
      "
          >
            <div
              className="
          rounded-xl px-5 py-4
          bg-white/95 text-[#2b3b4b] shadow-2xl backdrop-blur
          border border-white/60
          text-base sm:text-lg font-semibold
          max-w-md w-full text-center
        "
              role="status"
              aria-live="polite"
            >
              {missedLegitMsg}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LeadCapture({ t, leadEndpoint }) {
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [leadStatus, setLeadStatus] = useState({ state: 'idle', message: '' });

  const updateLeadForm = (field) => (event) => {
    const value = event.target.value;
    setLeadForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitLead = async (event) => {
    event.preventDefault();
    if (leadStatus.state === 'sending') return;

    if (!leadForm.email.trim()) {
      setLeadStatus({ state: 'error', message: t('leadForm.errorEmail') });
      return;
    }

    setLeadStatus({ state: 'sending', message: '' });
    try {
      const res = await fetch(leadEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadForm.name.trim() || null,
          email: leadForm.email.trim(),
          company: leadForm.company.trim() || null,
          phone: leadForm.phone.trim() || null,
          message: leadForm.message.trim() || null,
          source: 'marketing',
          type: 'contact',
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'submit_failed');
      }

      setLeadForm({ name: '', email: '', company: '', phone: '', message: '' });
      setLeadStatus({ state: 'success', message: t('leadForm.success') });
    } catch (err) {
      setLeadStatus({ state: 'error', message: t('leadForm.error') });
    }
  };

  return (
    <div className="rounded-3xl bg-white text-slate-900 p-8 md:p-10 shadow-[0_30px_80px_rgba(15,23,42,0.45)]">
      <form onSubmit={submitLead} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-xs uppercase tracking-wide text-slate-500">
            <span className="block mb-1">{t('leadForm.name')}</span>
            <input
              type="text"
              value={leadForm.name}
              onChange={updateLeadForm('name')}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/15"
              placeholder={t('leadForm.namePlaceholder')}
              required
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-slate-500">
            <span className="block mb-1">{t('leadForm.email')}</span>
            <input
              type="email"
              value={leadForm.email}
              onChange={updateLeadForm('email')}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/15"
              placeholder={t('leadForm.emailPlaceholder')}
              required
            />
          </label>
        </div>
        <label className="text-xs uppercase tracking-wide text-slate-500">
          <span className="block mb-1">{t('leadForm.company')}</span>
            <input
              type="text"
              value={leadForm.company}
              onChange={updateLeadForm('company')}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/15"
              placeholder={t('leadForm.companyPlaceholder')}
              required
            />
          </label>
        <label className="text-xs uppercase tracking-wide text-slate-500">
          <span className="block mb-1">{t('leadForm.phone')}</span>
          <input
            type="tel"
            value={leadForm.phone}
            onChange={updateLeadForm('phone')}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/15"
            placeholder={t('leadForm.phonePlaceholder')}
            required
          />
        </label>
        <label className="text-xs uppercase tracking-wide text-slate-500">
          <span className="block mb-1">{t('leadForm.message')}</span>
          <textarea
            value={leadForm.message}
            onChange={updateLeadForm('message')}
            className="min-h-[110px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/15"
            placeholder={t('leadForm.messagePlaceholder')}
            required
          />
        </label>
        {leadStatus.state === 'success' && (
          <p className="text-xs text-emerald-600">{leadStatus.message}</p>
        )}
        {leadStatus.state === 'error' && (
          <p className="text-xs text-rose-600">{leadStatus.message}</p>
        )}
        <button
          type="submit"
          disabled={leadStatus.state === 'sending'}
          className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 text-sm font-semibold text-white h-11 transition hover:bg-slate-800 disabled:opacity-60"
        >
          {leadStatus.state === 'sending' ? t('leadForm.sending') : t('leadForm.submit')}
        </button>
      </form>
    </div>
  );
}

function Feature({ Icon, title, description, featured = false }) {
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
      transition={{ duration: 0.5 }}
      className={`p-6 rounded-xl shadow-md hover:shadow-lg transition h-full ${
        featured
          ? 'bg-[#193245] ring-1 ring-[#00aca9]/45'
          : 'bg-[#172a3a]'
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-6 h-6 text-white/90" aria-hidden="true" />
        <h3 className="font-semibold text-white text-base">{title}</h3>
      </div>
      <p className="text-sm text-white/70 leading-relaxed">{description}</p>
    </motion.div>
  );
}
